import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ── Types ────────────────────────────────────────────

export interface CatalogProduct {
  id: string;
  partnerId: string;
  name: string;
  brand: string | null;
  sku: string | null;
  partNumber: string | null;
  description: string | null;
  category: string | null;
  tags: string[] | null;
  photoS3Key: string | null;
  photoUrl: string | null;
  price: string | null;
  buyUrl: string | null;
  deliveryDaysMin: number | null;
  deliveryDaysMax: number | null;
  compatibleVehicleIds: string[] | null;
  compatibleMakes: string[] | null;
  compatibleModels: string[] | null;
  active: boolean;
  outOfStock: boolean;
  totalImpressions: number;
  totalClicks: number;
  activeCampaignsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogListResponse {
  products: CatalogProduct[];
  total: number;
  page: number;
  limit: number;
}

export interface CatalogDetailResponse {
  product: CatalogProduct;
  campaigns: {
    campaignProductId: string;
    campaignId: string;
    campaignName: string;
    priceOverride: string | null;
    deliveryOverride: string | null;
    active: boolean;
  }[];
}

export interface LinkedProduct {
  id: string;
  campaignId: string;
  partnerProductId: string | null;
  name: string;
  sku: string | null;
  photoUrl: string | null;
  catalogPrice: string | null;
  priceOverride: string | null;
  effectivePrice: string | null;
  deliveryDaysMin: number | null;
  deliveryDaysMax: number | null;
  deliveryOverride: string | null;
  buyUrl: string | null;
  active: boolean;
  outOfStock: boolean;
  clicks: number;
  hasPriceOverride: boolean;
}

export interface CatalogSuggestion {
  priority: number;
  products: CatalogProduct[];
}

export interface PreviewC3 {
  preview: {
    productName: string;
    photoUrl: string | null;
    price: number | null;
    savings: number | null;
    deliveryDaysMin: number | null;
    deliveryDaysMax: number | null;
    buyUrl: string | null;
    partnerName: string;
    sponsored: true;
  };
}

// ── Catalog CRUD ─────────────────────────────────────

export interface CatalogListParams {
  search?: string;
  category?: string;
  status?: 'active' | 'paused' | 'out_of_stock' | 'no_campaign';
  page?: number;
  limit?: number;
}

export function useCatalogList(pid: string, params: CatalogListParams = {}) {
  return useQuery<CatalogListResponse>({
    queryKey: ['catalog', pid, params],
    queryFn: async () => {
      const res = await api.get(`/partners/${pid}/catalog`, { params });
      return res.data as CatalogListResponse;
    },
    enabled: !!pid,
  });
}

export function useCatalogDetail(pid: string, productId: string) {
  return useQuery<CatalogDetailResponse>({
    queryKey: ['catalog-detail', pid, productId],
    queryFn: async () => {
      const res = await api.get(`/partners/${pid}/catalog/${productId}`);
      return res.data as CatalogDetailResponse;
    },
    enabled: !!pid && !!productId,
  });
}

export function useCreateCatalogProduct(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      brand?: string;
      sku?: string;
      partNumber?: string;
      description?: string;
      category: string;
      price: number;
      buyUrl: string;
      deliveryDaysMin?: number;
      deliveryDaysMax?: number;
      compatibleVehicleIds?: string[];
      compatibleMakes?: string[];
      compatibleModels?: string[];
      tags?: string[];
    }) => {
      const res = await api.post(`/partners/${pid}/catalog`, data);
      return (res.data as { product: CatalogProduct }).product;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', pid] });
    },
  });
}

export function useUpdateCatalogProduct(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, ...data }: {
      productId: string;
      name?: string;
      brand?: string | null;
      sku?: string | null;
      partNumber?: string | null;
      description?: string | null;
      category?: string;
      price?: number;
      buyUrl?: string;
      deliveryDaysMin?: number | null;
      deliveryDaysMax?: number | null;
      compatibleVehicleIds?: string[] | null;
      compatibleMakes?: string[] | null;
      compatibleModels?: string[] | null;
      tags?: string[] | null;
      active?: boolean;
      outOfStock?: boolean;
    }) => {
      const res = await api.patch(`/partners/${pid}/catalog/${productId}`, data);
      return (res.data as { product: CatalogProduct }).product;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['catalog', pid] });
      qc.invalidateQueries({ queryKey: ['catalog-detail', pid, vars.productId] });
    },
  });
}

export function useDeleteCatalogProduct(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (productId: string) => {
      const res = await api.delete(`/partners/${pid}/catalog/${productId}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', pid] });
    },
  });
}

export function useImportCatalogCsv(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const csvContent = await file.text();
      const res = await api.post(`/partners/${pid}/catalog/import`, { csvContent });
      return res.data as { imported: number; errors: { row: number; field: string; message: string }[] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', pid] });
    },
  });
}

export function useExportCatalogCsv(pid: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get(`/partners/${pid}/catalog/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'catalogo.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function usePresignCatalogPhoto(pid: string) {
  return useMutation({
    mutationFn: async (ext: 'jpg' | 'jpeg' | 'png' | 'webp') => {
      const res = await api.post(`/partners/${pid}/catalog/presign-photo`, { ext });
      return res.data as { uploadUrl: string; s3Key: string };
    },
  });
}

// ── Campaign ↔ Catalog link ─────────────────────────

export function useCampaignLinkedProducts(pid: string, cid: string) {
  return useQuery<LinkedProduct[]>({
    queryKey: ['campaign-linked-products', pid, cid],
    queryFn: async () => {
      const res = await api.get(`/partners/${pid}/campaigns/${cid}/products`);
      return (res.data as { products: LinkedProduct[] }).products;
    },
    enabled: !!pid && !!cid,
  });
}

export function useLinkProducts(pid: string, cid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (productIds: string[]) => {
      const res = await api.post(`/partners/${pid}/campaigns/${cid}/products/link`, { productIds });
      return res.data as { linked: number; alreadyLinked: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-linked-products', pid, cid] });
      qc.invalidateQueries({ queryKey: ['catalog', pid] });
    },
  });
}

export function useUnlinkProduct(pid: string, cid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (campaignProductId: string) => {
      await api.delete(`/partners/${pid}/campaigns/${cid}/products/${campaignProductId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-linked-products', pid, cid] });
      qc.invalidateQueries({ queryKey: ['catalog', pid] });
    },
  });
}

export function useUpdateCampaignProduct(pid: string, cid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignProductId, ...data }: {
      campaignProductId: string;
      active?: boolean;
      outOfStock?: boolean;
      priceOverride?: number | null;
      deliveryOverride?: string | null;
    }) => {
      await api.patch(`/partners/${pid}/campaigns/${cid}/products/${campaignProductId}`, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-linked-products', pid, cid] });
    },
  });
}

export function useCatalogSuggestions(pid: string, cid: string) {
  return useQuery<CatalogSuggestion[]>({
    queryKey: ['catalog-suggestions', pid, cid],
    queryFn: async () => {
      const res = await api.get(`/partners/${pid}/campaigns/${cid}/catalog-suggestions`);
      return (res.data as { suggestions: CatalogSuggestion[] }).suggestions;
    },
    enabled: !!pid && !!cid,
  });
}

export function usePreviewC3(pid: string, productId: string, basePrice?: number) {
  return useQuery<PreviewC3>({
    queryKey: ['preview-c3', pid, productId, basePrice],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (basePrice != null) params['basePrice'] = String(basePrice);
      const res = await api.get(`/partners/${pid}/catalog/${productId}/preview-c3`, { params });
      return res.data as PreviewC3;
    },
    enabled: !!pid && !!productId,
  });
}
